import { getPeriodStartEnd } from '@dolfin/utils/dates'
import { parseISO } from 'date-fns'
import { DolfinDiagram } from '../diagram'
import { DolfinExecution } from '../execution'
import DolfinCompensationRulesFlowExecutor from '../flow-executor/compensation-rules'
import { MultiplierOutput, NodeError, NodeWarning } from '../types'
import { getCompensationValue } from '../utils'
import { DolfinNode } from './base'
import { TypedNode } from './types'

/**
 * Multiplier node - applies a multiplier to the current compensation value.
 *
 * Uses tiered structure based on target achievement or metric value.
 *
 * @see /docs/domain/nodes/multiplier.md for canonical documentation
 */
export class MultiplierNode extends DolfinNode<DolfinCompensationRulesFlowExecutor, 'multiplier'> {
  private getCompensationValuesPeriodCandidates(multiplier: number): MultiplierOutput {
    if (!this.node.data.parsedValues?.compensationValuesPeriodCandidates) {
      return []
    }

    return [
      {
        name: 'compensation_values_period_candidates',
        value: this.node.data.parsedValues.compensationValuesPeriodCandidates.map(
          compensationValue => ({
            profile_id: compensationValue.profile_id,
            value: compensationValue.value * multiplier
          })
        )
      }
    ]
  }

  private getCompensationValuePeriodCandidate(multiplier: number): MultiplierOutput {
    if (this.node.data.parsedValues?.compensationValuePeriodCandidate === undefined) {
      return []
    }

    return [
      {
        name: 'compensation_value_period_candidate',
        value: this.node.data.parsedValues.compensationValuePeriodCandidate * multiplier
      }
    ]
  }

  async process(): Promise<MultiplierOutput> {
    const formData = this.node.data.formData
    if ('type' in formData && formData.type === 'straight') {
      return this.processStraight(formData)
    }

    let attainment = 0

    if (!('reference_type' in formData)) {
      return [
        {
          name: 'multiplier',
          value: 1
        }
      ]
    }

    const tieredFormData = formData

    if (tieredFormData.reference_type === 'target') {
      let periodStart: string | undefined
      let periodEnd: string | undefined

      // In record-based rules, target tiers are evaluated as of this record
      // (unless full_period_achievement opts into the complete period). Use
      // the executor's configured date field, matching the shared target
      // achievement lookup and commission node behavior.
      if (!this.flowExecutor.compensationRule.outcome_period) {
        const processingDate = this.getRecordProcessingDate()
        if (!processingDate) {
          console.warn('Processing date not found: ', this.flowExecutor.dateField)
          return [{ name: 'multiplier', value: 1 }]
        }

        if (tieredFormData.full_period_achievement) {
          const target = await this.flowExecutor.rpcClient.getTarget(tieredFormData.target.id)
          if (!target) {
            console.warn('Target not found: ', tieredFormData.target.id)
            return [{ name: 'multiplier', value: 1 }]
          }

          // Without explicit bounds, the record lookup sends an epoch end date.
          // That is safe only with the record cutoff; full-period evaluation
          // must supply the target period end, as condition variables do.
          const targetPeriod = getPeriodStartEnd(target.period, parseISO(processingDate))
          periodStart = targetPeriod.start
          periodEnd = targetPeriod.end
        }
      }

      attainment =
        (
          await this.getPeriodTargetAchievement(
            tieredFormData.target.id,
            periodStart,
            periodEnd,
            !!tieredFormData.full_period_achievement
          )
        )?.achievement ?? 0
    } else if (
      tieredFormData.reference_type === 'metric' &&
      this.flowExecutor.compensationRule.outcome_period
    ) {
      attainment = await this.getMetricReferenceValue(
        tieredFormData.metric,
        this.flowExecutor.period!.start,
        this.flowExecutor.period!.end
      )
    }

    const attainmentToCompare = attainment * (tieredFormData.reference_type === 'target' ? 100 : 1)
    console.info('Attainment:', attainmentToCompare)

    const tiers = [
      ...(tieredFormData.tiers as {
        attainment: number
        value?: string | number
        multiplier?: number
      }[])
    ].sort((a, b) => b.attainment - a.attainment)
    for (const tier of tiers) {
      if (attainmentToCompare >= tier.attainment) {
        const multiplier = tier.value
          ? getCompensationValue(tier.value, attainmentToCompare)
          : (tier.multiplier ?? 1)
        const tierIndex = (
          tieredFormData.tiers as {
            attainment: number
            value: number
          }[]
        ).findIndex(t => t.attainment === tier.attainment)

        console.table({
          'Tier Index': tierIndex,
          'Tier Attainment': tier.attainment,
          Attainment: attainmentToCompare
        })

        if (this.node.data.parsedValues?.compensationValues) {
          return [
            {
              name: 'tier_index',
              value: tierIndex
            },
            {
              name: 'attainment',
              value: attainment
            },
            {
              name: 'multiplier',
              value: multiplier
            },
            {
              name: 'compensation_values',
              value: (
                this.node.data.parsedValues?.compensationValues as {
                  profile_id: string
                  value: number
                }[]
              ).map(compensationValue => ({
                profile_id: compensationValue.profile_id,
                value: compensationValue.value * multiplier
              }))
            },
            ...this.getCompensationValuesPeriodCandidates(multiplier)
          ]
        } else {
          return [
            {
              name: 'tier_index',
              value: tierIndex
            },
            {
              name: 'attainment',
              value: attainment
            },
            {
              name: 'multiplier',
              value: multiplier
            },
            {
              name: 'compensation_value',
              value: (this.node.data.parsedValues?.compensationValue ?? 0) * multiplier
            },
            ...this.getCompensationValuePeriodCandidate(multiplier)
          ]
        }
      }
    }

    return [
      {
        name: 'multiplier',
        value: 1
      }
    ]
  }

  private processStraight(formData: { multiplier_value?: number }): MultiplierOutput {
    const multiplier = formData.multiplier_value ?? 1

    if (this.node.data.parsedValues?.compensationValues) {
      return [
        {
          name: 'multiplier',
          value: multiplier
        },
        {
          name: 'compensation_values',
          value: (
            this.node.data.parsedValues.compensationValues as {
              profile_id: string
              value: number
            }[]
          ).map(compensationValue => ({
            profile_id: compensationValue.profile_id,
            value: compensationValue.value * multiplier
          }))
        },
        ...this.getCompensationValuesPeriodCandidates(multiplier)
      ]
    }

    return [
      {
        name: 'multiplier',
        value: multiplier
      },
      {
        name: 'compensation_value',
        value: (this.node.data.parsedValues?.compensationValue ?? 0) * multiplier
      },
      ...this.getCompensationValuePeriodCandidate(multiplier)
    ]
  }

  async parse(_: DolfinDiagram, execution?: DolfinExecution): Promise<TypedNode<'multiplier'>> {
    this.parseCompensationValues(execution)

    return this.node
  }

  static validate(
    node: TypedNode<'multiplier'>,
    diagram: DolfinDiagram
  ): {
    errors: NodeError[]
    warnings: NodeWarning[]
  } {
    const superValidation = super.validate(node, diagram)

    return {
      errors: [...superValidation.errors],
      warnings: [...superValidation.warnings]
    }
  }
}
